import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Pagination from "../../components/Pagination";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const ITEMS_PER_PAGE = 10;
const defaultEditForm = {
  title: "",
  moduleType: "",
  language: "ENG",
  durationMinutes: "",
  passingScore: 80,
  deadline: "",
};

const formatDuration = (minutes) => {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "-";
  const hours = Math.floor(value / 60);
  const remainingMinutes = value % 60;
  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} hr ${remainingMinutes} min`;
  }
  if (hours > 0) return `${hours} hr`;
  return `${remainingMinutes} min`;
};

export default function CustomExternalTrainingTab() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingCourse, setEditingCourse] = useState(null);
  const [editForm, setEditForm] = useState(defaultEditForm);
  const [saving, setSaving] = useState(false);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const loadCourses = useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/custom-external-trainings/courses`, {
        headers: authHeader(),
      });
      setCourses(res.data || []);
    } catch (err) {
      console.error("Failed to load custom external trainings", err);
      setError("Failed to load custom external trainings.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (event.data?.type !== "custom-external-training-close") return;
      setPreviewCourse(null);
      setPreviewUrl("");
    };

    window.addEventListener("message", handlePreviewMessage);
    return () => window.removeEventListener("message", handlePreviewMessage);
  }, []);

  const filteredCourses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter((course) =>
      [
        course.cet_title,
        course.cet_provider,
        course.cet_description,
        course.cet_language,
        course.cet_duration_minutes,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [courses, search]);

  const pagedCourses = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredCourses.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCourses, page]);

  const openPreview = async (course) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/custom-external-trainings/${course.cet_id}/launch`,
        { headers: authHeader() },
      );
      const launchUrl = res.data?.launchUrl;
      if (!launchUrl) throw new Error("Missing launch URL");
      const separator = launchUrl.includes("?") ? "&" : "?";
      setPreviewCourse(course);
      setPreviewUrl(`${BASE_URL}${launchUrl}${separator}embedded=1`);
    } catch (err) {
      console.error("Failed to open custom external training preview", err);
      alert("Failed to open the preview. Please try again.");
    }
  };

  const openEdit = (course) => {
    setEditingCourse(course);
    setEditForm({
      title: course.cet_title || "",
      moduleType: course.cet_provider || "",
      language: course.cet_language || "ENG",
      durationMinutes: course.cet_duration_minutes ?? "",
      passingScore: course.cet_passing_score ?? 80,
      deadline: course.cet_deadline || "",
    });
  };

  const saveEdit = async () => {
    const passingScore = Number(editForm.passingScore);
    const durationMinutes =
      editForm.durationMinutes === "" ? "" : Number(editForm.durationMinutes);

    if (!editForm.title.trim()) {
      alert("Training title is required.");
      return;
    }
    if (!editForm.moduleType.trim()) {
      alert("Module type is required.");
      return;
    }
    if (
      editForm.durationMinutes !== "" &&
      (!Number.isInteger(durationMinutes) || durationMinutes < 1)
    ) {
      alert("Duration must be a whole number greater than zero.");
      return;
    }
    if (
      !Number.isInteger(passingScore) ||
      passingScore < 0 ||
      passingScore > 100
    ) {
      alert("Passing score must be a whole number from 0 to 100.");
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        `${BASE_URL}/api/custom-external-trainings/courses/${editingCourse.cet_id}`,
        {
          title: editForm.title.trim(),
          moduleType: editForm.moduleType.trim(),
          language: editForm.language,
          durationMinutes: editForm.durationMinutes,
          passingScore,
          deadline: editForm.deadline || null,
        },
        { headers: authHeader() },
      );
      setEditingCourse(null);
      setEditForm(defaultEditForm);
      await loadCourses();
    } catch (err) {
      console.error("Failed to update custom external training", err);
      alert(err.response?.data?.message || "Failed to update training.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading custom external trainings...</div>;

  return (
    <>
    <fieldset className="border p-3 my-3 custom-external-training-panel">
      <legend>Custom External Training</legend>

      <div className="d-flex justify-content-between align-items-end gap-3 mb-3">
        <div>
          <div className="fw-semibold">Managed custom external training apps</div>
          <div className="text-muted small">
            Source files are stored separately under backend/custom-external-trainings.
          </div>
        </div>
        <div className="nw-filter-bar" style={{ minWidth: 300 }}>
          <input
            className="form-control"
            placeholder="Search custom trainings..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 80 }}>Order</th>
              <th>Training Name</th>
              <th>Module Type</th>
              <th>Language</th>
              <th>Duration</th>
              <th className="text-center" style={{ width: 120 }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedCourses.map((course) => (
              <tr key={course.cet_id}>
                <td>{course.cet_order}</td>
                <td>
                  <div className="fw-semibold">{course.cet_title}</div>
                  {course.cet_description && (
                    <div className="text-muted small">{course.cet_description}</div>
                  )}
                </td>
                <td>{course.cet_provider || "-"}</td>
                <td>{course.cet_language || "ENG"}</td>
                <td>{formatDuration(course.cet_duration_minutes)}</td>
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => openPreview(course)}
                    title={`Preview ${course.cet_title}`}
                    aria-label={`Preview ${course.cet_title}`}
                  >
                    <i className="bi bi-play-fill" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary ms-2"
                    onClick={() => openEdit(course)}
                    title={`Edit ${course.cet_title}`}
                    aria-label={`Edit ${course.cet_title}`}
                  >
                    <i className="bi bi-pencil" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            ))}
            {pagedCourses.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted">
                  No custom external trainings found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={filteredCourses.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setPage}
      />
    </fieldset>

    <Modal
      show={Boolean(editingCourse)}
      onHide={() => {
        setEditingCourse(null);
        setEditForm(defaultEditForm);
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Custom External Training</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Title</label>
          <input
            className="form-control"
            value={editForm.title}
            onChange={(event) =>
              setEditForm((prev) => ({ ...prev, title: event.target.value }))
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Module Type</label>
          <input
            className="form-control"
            value={editForm.moduleType}
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                moduleType: event.target.value,
              }))
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Language</label>
          <select
            className="form-select"
            value={editForm.language}
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                language: event.target.value,
              }))
            }
          >
            <option value="ENG">ENG</option>
            <option value="FR">FR</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Duration (minutes)</label>
          <input
            type="number"
            className="form-control"
            min="1"
            step="1"
            value={editForm.durationMinutes}
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                durationMinutes: event.target.value,
              }))
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Passing Score (%)</label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            step="1"
            value={editForm.passingScore}
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                passingScore: event.target.value,
              }))
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Deadline</label>
          <input
            type="date"
            className="form-control"
            value={editForm.deadline}
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                deadline: event.target.value,
              }))
            }
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            setEditingCourse(null);
            setEditForm(defaultEditForm);
          }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button variant="primary" onClick={saveEdit} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal
      show={Boolean(previewCourse)}
      onHide={() => {
        setPreviewCourse(null);
        setPreviewUrl("");
      }}
      size="xl"
      centered
      dialogClassName="custom-external-preview-modal"
      contentClassName="border-0 bg-transparent shadow-none"
    >
      <Modal.Body className="p-0 position-relative">
        <button
          type="button"
          className="btn-close"
          aria-label="Close preview"
          onClick={() => {
            setPreviewCourse(null);
            setPreviewUrl("");
          }}
          style={{
            position: "absolute",
            top: "-28px",
            right: 0,
            zIndex: 2,
            backgroundColor: "#fff",
            borderRadius: "50%",
            padding: "10px",
            opacity: 1,
          }}
        />
        {previewUrl && (
          <iframe
            title={previewCourse?.cet_title || "Custom external training preview"}
            src={previewUrl}
            allow="autoplay; fullscreen"
            allowFullScreen
            style={{
              width: "960px",
              maxWidth: "100%",
              height: "652px",
              border: 0,
              display: "block",
              margin: "0 auto",
              background: "transparent",
            }}
          />
        )}
      </Modal.Body>
    </Modal>
    </>
  );
}
